import React, { useEffect, useMemo } from 'react';
import { isEqual } from 'lodash';
import { useProductDetailsPageContext } from '../ProductDetailsPageContext/ProductDetailsPageContext';
import useShopProductQuery from '../useShopProductQuery';
import useShopProductQueryPriceAndInventory from '../useShopProductQueryPriceAndInventory';
import useShopProductVariantBySelectedOptionsQuery from '../useShopProductVariantBySelectedOptions';
import useShopProductColorOptionsQuery from '../useShopProductColorOptions';
import useShopProductSizeOptionsQuery from '../useShopProductSizeOptions';
import useShopProductInseamOptionsQuery from '../useShopProductInseamOptions';
import useShopWaysToWearItQuery from '../useShopWaysToWearItQuery';
import { featureFlags as currentFeatureFlags } from '../../../helpers/featureFlags';
import useShopRecommendedCollectionsQuery from '../useShopRecommendedCollectionsQuery';
import useShopCategoriesQuery from '../useShopCategoriesQuery';
import useCreditIncentivesEligibilityQuery from '../useGetCreditIncentivesEligibilityQuery';
import useShoppingAcrossBusinessLinesEligibilityQuery from '../useGetShoppingAcrossBusinessLinesEligibilityQuery';
import useStitchFixClientQuery from '../useStitchFixClient';
import {
  FeatureFlagNames,
  FeatureFlags,
  useFeatureFlags,
} from '../useFeatureFlags';
import useShopProductRecommendedColorOptionsQuery from '../useShopProductRecommendedColorOptions';
import useShopRecommendedSizeQuery from '../useShopRecommendedSizeQuery';
import useShopProductPurchaseOptions from '../useShopProductPurchaseOptions';
import useShopMatchingSetQuery from '../useShopMatchingSetQuery';

const ProductDetailsPageGraphQlConsumer = ({
  children,
}: {
  children: JSX.Element;
}) => {
  const [state, dispatch] = useProductDetailsPageContext();

  const skuIdForShopProductQueries =
    state.selectedSkuId || state.fallbackSkuId || state.productSkuId;

  const useShopProductQueryArguments = useMemo(
    () => ({
      productSkuId: skuIdForShopProductQueries,
      externalProductId: state.externalProductId,
      gated: state.gated,
    }),
    [skuIdForShopProductQueries, state.externalProductId, state.gated],
  );

  const runUseShopProductQuery = useMemo(
    () =>
      !isEqual(
        state.previousUseShopProductQueryArguments,
        useShopProductQueryArguments,
      ),
    [state.previousUseShopProductQueryArguments, useShopProductQueryArguments],
  );

  useEffect(() => {
    if (runUseShopProductQuery) {
      dispatch({
        type: 'SET_PREVIOUS_USE_SHOP_PRODUCT_QUERY_ARGUMENTS',
        payload: useShopProductQueryArguments,
      });
    }
  }, [runUseShopProductQuery, useShopProductQueryArguments, dispatch]);

  const { shopProductData, shopProductLoading, shopProductError } =
    useShopProductQuery({
      ...useShopProductQueryArguments,
      skip: !runUseShopProductQuery && !!state.shopProduct,
    });

  const { clientData, clientLoading } = useStitchFixClientQuery();

  const {
    shopProductPriceAndInventoryData,
    shopProductPriceAndInventoryLoading,
    shopProductPriceAndInventoryError,
  } = useShopProductQueryPriceAndInventory({
    productSkuId:
      state.selectedSkuId || state.fallbackSkuId || state.productSkuId,
    externalProductId: state.externalProductId,
  });

  const {
    shopProductVariantBySelectedOptionsData,
    shopProductVariantBySelectedOptionsLoading,
    shopProductVariantBySelectedOptionsError,
  } = useShopProductVariantBySelectedOptionsQuery({
    skuId: skuIdForShopProductQueries,
    externalProductId: state.externalProductId,
    uniqueColorId: state.selectedUniqueColorId || state.fallbackUniqueColorId,
    size: state.selectedSizeLabel || state.fallbackSizeLabel,
    inseam: state.selectedInseamLabel || state.fallbackInseamLabel,
  });

  const {
    shopProductColorOptionsData,
    shopProductColorOptionsLoading,
    shopProductColorOptionsError,
  } = useShopProductColorOptionsQuery({
    skuId: skuIdForShopProductQueries,
    externalProductId: state.externalProductId,
    size: state.onRecommendedSizesTab
      ? state.recommendedSkuSizeLabel
      : state.selectedSizeLabel || state.fallbackSizeLabel,
  });

  const {
    shopProductSizeOptionsData,
    shopProductSizeOptionsLoading,
    shopProductSizeOptionsError,
  } = useShopProductSizeOptionsQuery({
    skuId: skuIdForShopProductQueries,
    externalProductId: state.externalProductId,
    uniqueColorId: state.selectedUniqueColorId || state.fallbackUniqueColorId,
  });

  const {
    shopProductInseamOptionsData,
    shopProductInseamOptionsLoading,
    shopProductInseamOptionsError,
  } = useShopProductInseamOptionsQuery({
    skuId: skuIdForShopProductQueries,
    externalProductId: state.externalProductId,
    uniqueColorId: state.selectedUniqueColorId || state.fallbackUniqueColorId,
    size: state.selectedSizeLabel || state.fallbackSizeLabel,
  });

  const {
    shopWaysToWearItData,
    shopWaysToWearItLoading,
    shopWaysToWearItError,
  } = useShopWaysToWearItQuery({
    skuId: state.selectedSkuId || state.fallbackSkuId || '',
    businessLine: state.productBusinessLine,
    outfitIdFromContext: state.pdpOutfitId,
  });

  const { shopMatchingSetData, shopMatchingSetLoading, shopMatchingSetError } =
    useShopMatchingSetQuery({
      skuId: state.selectedSkuId || state.fallbackSkuId || '',
    });

  const {
    shopRecommendedCollectionsData,
    shopRecommendedCollectionsLoading,
    shopRecommendedCollectionsError,
  } = useShopRecommendedCollectionsQuery({
    skuId: state.selectedSkuId || state.fallbackSkuId || '',
    businessLine: state.productBusinessLine,
    ungated: !state.gated,
  });

  const { shopCategoriesData, shopCategoriesLoading, shopCategoriesError } =
    useShopCategoriesQuery({
      businessLine: state.productBusinessLine,
      categoryId: state.categoryId,
      skip: !state.productBusinessLine,
      gated: state.gated,
    });

  const {
    creditIncentivesEligibilityData,
    creditIncentivesEligibilityLoading,
    creditIncentivesEligibilityError,
  } = useCreditIncentivesEligibilityQuery({
    // NOTE: These state variables are set from the use product query fetching results
    // The credit incentives query running is dependent on those results
    clientHasPurchases: state.clientHasPurchases,
    error: !!state.error,
    loading: state.loading,
    skip: state.gated,
  });

  const recommendedSizeLabel = () => {
    return state.recommendedSizes instanceof Array
      ? state.recommendedSizes[0]?.sku?.size ||
          state.recommendedSizes[0]?.shopProductBySkuId?.variantBySkuId?.size
            ?.name
      : null;
  };

  const {
    shoppingAcrossBusinessLinesEligibilityData,
    shoppingAcrossBusinessLinesEligibilityLoading,
    shoppingAcrossBusinessLinesEligibilityError,
  } = useShoppingAcrossBusinessLinesEligibilityQuery({
    clientId: clientData?.client?.id,
    error: !!state.error,
    loading: state.loading,
  });

  const {
    shopProductRecommendedColorOptionsData,
    shopProductRecommendedColorOptionsLoading,
    shopProductRecommendedColorOptionsError,
  } = useShopProductRecommendedColorOptionsQuery({
    skuId: state.recommendedSkuId,
    externalProductId: state.externalProductId,
    size: recommendedSizeLabel(),
    skip:
      !state.recommendedSkuId ||
      state.recommendedSizes.length < 1 ||
      !state.gated,
  });

  const {
    shopRecommendedSizeData,
    shopRecommendedSizeLoading,
    shopRecommendedSizeError,
  } = useShopRecommendedSizeQuery({
    skuId: state.recommendedSkuId || '',
    skip: !state.recommendedSkuId || !state.gated,
  });

  const {
    shopProductPurchaseOptionsData,
    shopProductPurchaseOptionsLoading,
    shopProductPurchaseOptionsError,
  } = useShopProductPurchaseOptions({
    skuId: state.selectedSkuId || '',
    skip: !state.selectedSkuId || !state.gated,
  });

  const { featureFlags } = useFeatureFlags(
    currentFeatureFlags as FeatureFlagNames,
  );

  const { featureFlags: visitorFeatureFlags } = useFeatureFlags(
    currentFeatureFlags as FeatureFlagNames,
    'visitor',
  );

  useEffect(() => {
    dispatch({
      type: 'SET_FEATURE_FLAGS',
      payload: { featureFlags: featureFlags as FeatureFlags },
    });
  }, [featureFlags, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'SET_VISITOR_FEATURE_FLAGS',
      payload: { featureFlags: visitorFeatureFlags as FeatureFlags },
    });
  }, [visitorFeatureFlags, dispatch]);

  useEffect(() => {
    if (shopProductData || shopProductError) {
      dispatch({
        type: 'SET_SHOP_PRODUCT_QUERY_DATA',
        payload: {
          shopProductData,
          shopProductLoading,
          shopProductError,
        },
      });
    }
  }, [shopProductData, shopProductLoading, shopProductError, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'SET_CLIENT_DATA',
      payload: {
        clientData,
        clientLoading,
        productBusinessLine: state.productBusinessLine,
      },
    });
  }, [clientData, clientLoading, dispatch, state.productBusinessLine]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_PRICE_AND_INVENTORY_QUERY_DATA',
      payload: {
        shopProductPriceAndInventoryData,
        shopProductPriceAndInventoryLoading,
        shopProductPriceAndInventoryError,
      },
    });
  }, [
    shopProductPriceAndInventoryData,
    shopProductPriceAndInventoryLoading,
    shopProductPriceAndInventoryError,
    dispatch,
  ]);

  useEffect(() => {
    if (state.selectedSizeLabel) {
      dispatch({
        type: 'SET_SHOP_PRODUCT_SELECTED_VARIANT',
        payload: {
          selectedVariantData: shopProductVariantBySelectedOptionsData,
          selectedVariantLoading: shopProductVariantBySelectedOptionsLoading,
          selectedVariantError: shopProductVariantBySelectedOptionsError,
        },
      });
    } else {
      dispatch({
        type: 'SET_SHOP_PRODUCT_FALLBACK_VARIANT',
        payload: {
          fallbackVariantData: shopProductVariantBySelectedOptionsData,
          fallbackVariantLoading: shopProductVariantBySelectedOptionsLoading,
          fallbackVariantError: shopProductVariantBySelectedOptionsError,
        },
      });
    }
  }, [
    shopProductVariantBySelectedOptionsData,
    shopProductVariantBySelectedOptionsLoading,
    shopProductVariantBySelectedOptionsError,
    dispatch,
    state.selectedSizeLabel,
    state.fallbackSizeLabel,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_COLOR_OPTIONS',
      payload: {
        selectedColorOptionsData: shopProductColorOptionsData,
        selectedColorOptionsLoading: shopProductColorOptionsLoading,
        selectedColorOptionsError: shopProductColorOptionsError,
      },
    });
  }, [
    shopProductColorOptionsData,
    shopProductColorOptionsLoading,
    shopProductColorOptionsError,
    dispatch,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_SIZE_OPTIONS',
      payload: {
        selectedSizeOptionsData: shopProductSizeOptionsData,
        selectedSizeOptionsLoading: shopProductSizeOptionsLoading,
        selectedSizeOptionsError: shopProductSizeOptionsError,
      },
    });
  }, [
    shopProductSizeOptionsData,
    shopProductSizeOptionsLoading,
    shopProductSizeOptionsError,
    dispatch,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_INSEAM_OPTIONS',
      payload: {
        selectedInseamOptionsData: shopProductInseamOptionsData,
        selectedInseamOptionsLoading: shopProductInseamOptionsLoading,
        selectedInseamOptionsError: shopProductInseamOptionsError,
      },
    });
  }, [
    shopProductInseamOptionsData,
    shopProductInseamOptionsLoading,
    shopProductInseamOptionsError,
    dispatch,
  ]);

  useEffect(() => {
    if (shopWaysToWearItData)
      dispatch({
        type: 'SET_SHOP_WAYS_TO_WEAR_IT_QUERY_DATA',
        payload: {
          shopWaysToWearItData,
          shopWaysToWearItLoading,
          shopWaysToWearItError,
        },
      });
  }, [
    shopWaysToWearItData,
    shopWaysToWearItLoading,
    shopWaysToWearItError,
    dispatch,
  ]);

  useEffect(() => {
    if (shopMatchingSetData)
      dispatch({
        type: 'SET_SHOP_MATCHING_SET_QUERY_DATA',
        payload: {
          shopMatchingSetData,
          shopMatchingSetLoading,
          shopMatchingSetError,
        },
      });
  }, [
    shopMatchingSetData,
    shopMatchingSetLoading,
    shopMatchingSetError,
    dispatch,
  ]);

  useEffect(() => {
    if (shopRecommendedCollectionsData)
      dispatch({
        type: 'SET_SHOP_RECOMMENDED_COLLECTIONS_QUERY_DATA',
        payload: {
          shopRecommendedCollectionsData,
          shopRecommendedCollectionsLoading,
          shopRecommendedCollectionsError,
        },
      });
  }, [
    shopRecommendedCollectionsData,
    shopRecommendedCollectionsLoading,
    shopRecommendedCollectionsError,
    dispatch,
  ]);

  useEffect(() => {
    if (shopCategoriesData)
      dispatch({
        type: 'SET_SHOP_CATEGORIES_QUERY_DATA',
        payload: {
          shopCategoriesData,
          shopCategoriesLoading,
          shopCategoriesError,
        },
      });
  }, [
    shopCategoriesData,
    shopCategoriesLoading,
    shopCategoriesError,
    dispatch,
  ]);

  useEffect(() => {
    if (creditIncentivesEligibilityData)
      dispatch({
        type: 'SET_CREDIT_INCENTIVES_ELIGIBILITY_QUERY_DATA',
        payload: {
          creditIncentivesEligibilityData,
          creditIncentivesEligibilityLoading,
          creditIncentivesEligibilityError,
        },
      });
  }, [
    creditIncentivesEligibilityData,
    creditIncentivesEligibilityLoading,
    creditIncentivesEligibilityError,
    dispatch,
  ]);

  useEffect(() => {
    if (shoppingAcrossBusinessLinesEligibilityData)
      dispatch({
        type: 'SET_SHOPPING_ACROSS_BUSINESS_LINES_ELIGIBILITY_QUERY_DATA',
        payload: {
          shoppingAcrossBusinessLinesEligibilityData,
          shoppingAcrossBusinessLinesEligibilityLoading,
          shoppingAcrossBusinessLinesEligibilityError,
        },
      });
  }, [
    shoppingAcrossBusinessLinesEligibilityData,
    shoppingAcrossBusinessLinesEligibilityLoading,
    shoppingAcrossBusinessLinesEligibilityError,
    dispatch,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_RECOMMENDED_SIZES_QUERY_DATA',
      payload: {
        recommendedSizes: shopRecommendedSizeData,
        recommendedSizesLoading: shopRecommendedSizeLoading,
        recommendedSizesError: shopRecommendedSizeError,
      },
    });
  }, [
    shopRecommendedSizeData,
    dispatch,
    shopRecommendedSizeLoading,
    shopRecommendedSizeError,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_PURCHASE_OPTIONS',
      payload: {
        purchaseOptionsLoading: shopProductPurchaseOptionsLoading,
        purchaseOptionsError: shopProductPurchaseOptionsError,
        purchaseOptionsData: shopProductPurchaseOptionsData,
      },
    });
  }, [
    shopProductPurchaseOptionsData,
    dispatch,
    shopProductPurchaseOptionsLoading,
    shopProductPurchaseOptionsError,
  ]);

  useEffect(() => {
    dispatch({
      type: 'SET_SHOP_PRODUCT_RECOMMENDED_COLOR_OPTIONS',
      payload: {
        recommendedColorOptionsData: shopProductRecommendedColorOptionsData,
        recommendedColorOptionsLoading:
          shopProductRecommendedColorOptionsLoading,
        recommendedColorOptionsError: shopProductRecommendedColorOptionsError,
      },
    });
  }, [
    shopProductRecommendedColorOptionsData,
    shopProductRecommendedColorOptionsLoading,
    shopProductRecommendedColorOptionsError,
    dispatch,
  ]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_OOS_DATA_SIZE_OPTIONS',
      payload: { shopSizeOptions: state.shopSizeOptions },
    });
  }, [state.shopSizeOptions, dispatch]);

  useEffect(() => {
    dispatch({
      type: 'UPDATE_OOS_DATA_SELECTED_SKU_ID',
      payload: { selectedSkuId: state.selectedSkuId },
    });
  }, [state.selectedSkuId, dispatch]);

  useEffect(() => {
    const inUnselectedState =
      !state.selectedSizeLabel && !state.selectedInseamLabel;

    if (
      (state.oosData?.selectedSkuId || inUnselectedState) &&
      state.oosData?.shopSizeOptions &&
      state.oosData?.shopSizeOptions.length > 0 &&
      !state.oosData?.tracked
    ) {
      dispatch({ type: 'TRACK_OUT_OF_STOCK' });
    }
  }, [
    state.oosData?.shopSizeOptions,
    state.shopSizeOptions,
    state.selectedSizeLabel,
    state.shopInseamOptions,
    state.selectedInseamLabel,
    state.oosData?.selectedSkuId,
    state.oosData?.tracked,
    dispatch,
  ]);

  return <>{children}</>;
};

export default ProductDetailsPageGraphQlConsumer;
